import React, { useState } from 'react';
import { Alert, Box, Container, Fade, Paper, Typography, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { Formik, Form } from 'formik';
import FormFieldOptions from '@acorn-portal/shared/src/components/inputs/FormFieldOptions';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	CustomPaymentMethodPayload,
	ExtendPaymentMethodOptions,
} from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import { PaymentSessionClient } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import { PaymentClient } from '@acorn-portal/shared/src/services/api';
import {
	buildCapturePaymentMethodCommand,
	CapturePaymentMethodInput,
} from '@acorn-portal/shared/src/utils/payment/build-capture-payment-method-command';
import DirectPaymentVendors from '@acorn-portal/shared/src/components/vendor/DirectPaymentVendors';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import BraintreeFormInput from '@acorn-portal/shared/src/components/payment/braintree-form-input';
import { LoadingButton } from '@mui/lab';
import { Payment } from '@mui/icons-material';
import { useBraintreeContext } from '@acorn-portal/shared/src/context/braintree-context';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

interface FormValues {
	saveCard: boolean;
	paymentPayload?: CustomPaymentMethodPayload;
}

const BackOfficePaymentSessionPage: React.FC = () => {
	const theme = useTheme();
	const query = useQuery();
	const navigate = useNavigate();

	const { instance, isPaymentDetailsValid } = useBraintreeContext();

	const sessionId = query.get('sessionid');
	const amount = query.get('amount');
	const ref = query.get('ref');
	const unsupportedOnPortal = query.get('no-registration') ?? 'false';

	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);

	if (!sessionId || !amount || !ref) {
		navigate(ROUTE_PATHS.unauthorisedError);
		return null;
	}

	const isQuote = ref?.startsWith('QU');

	const onPostPaymentDetails = async (formValues: FormValues) => {
		setError(undefined);
		setSubmitting(true);
		try {
			if (!instance) {
				throw Error('No instance available.');
			}

			const { clientIp } = await new PaymentClient(undefined, API).getClientData();

			const requestPayload: ExtendPaymentMethodOptions = {
				threeDSecure: {
					amount,
					challengeRequested: formValues.saveCard,
					additionalInformation: { ipAddress: clientIp },
				},
			};
			const payload = await instance.requestPaymentMethod(requestPayload);

			const captureCommand = buildCapturePaymentMethodCommand({
				sessionCorrelationId: sessionId,
				saveCard: formValues.saveCard,
				type: payload.type,
				nonce: payload.nonce,
				deviceData: payload.deviceData ?? undefined,
				details: payload.details as CapturePaymentMethodInput['details'],
			});

			if (!captureCommand) {
				setError('Unsupported payment method, please try another payment method.');
				return;
			}

			const paymentSessionClient = new PaymentSessionClient(undefined, API);
			await paymentSessionClient.captureSessionPaymentMethod(captureCommand);

			navigate(ROUTE_PATHS.backOfficePaymentSessionSuccess, {
				state: {
					unsupportedOnPortal: unsupportedOnPortal,
				},
				replace: true,
			});
		} catch (error) {
			console.error('Error processing payment details:', error);
			setError('Unable to process your payment details, please ensure your details are correct.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Fade in timeout={400}>
			<Paper
				elevation={1}
				sx={{
					margin: {
						xs: theme.spacing(1),
						sm: theme.spacing(1),
						md: theme.spacing(3),
					},
					borderRadius: '4px',
					padding: {
						xs: theme.spacing(1),
						sm: theme.spacing(1),
						md: theme.spacing(3),
					},
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<Container maxWidth='sm'>
					<Box my={4} textAlign='left'>
						<Typography variant='h5' gutterBottom>
							{isQuote ? 'Quote Payment' : 'Policy Payment'}
						</Typography>
						<Typography variant='body1' sx={{ mb: 2 }}>
							Thank you for continuing to set up your payment. Your {isQuote ? 'quote' : 'policy'} reference is {ref}.
						</Typography>
						<Typography variant='h6' color='secondary.main'>
							Amount to Pay: £{amount}
						</Typography>
						<Typography variant='body1' sx={{ mt: 2 }}>
							Please confirm your payment details below. This will ensure your {isQuote ? 'quote' : 'policy'} payment of{' '}
							<b>£{amount}</b> is processed securely and efficiently.
						</Typography>
					</Box>
				</Container>

				<Formik<FormValues> initialValues={{ saveCard: true }} onSubmit={onPostPaymentDetails}>
					{({ isValidating, isSubmitting, setFieldValue, values }) => (
						<Form>
							<FormikLogErrors />
							<FormFieldOptions
								name='saveCard'
								label=''
								options={[
									{
										label: (
											<Typography>
												<b>Save my card details</b> - We will keep your card details stored safely and securely to allow
												us to take any future payments
											</Typography>
										),
										value: true,
									},
									{
										label: (
											<Typography>
												<b>Don&apos;t save my card details</b> - We won&apos;t store your card details and you&apos;ll
												need to give us your payment card details every time you make a change
											</Typography>
										),
										value: false,
									},
								]}
								onChange={(event) => {
									const { value } = event.target;
									setFieldValue('saveCard', value === 'true');
								}}
								value={values.saveCard}
								noMarginBottom
							/>
							{error && (
								<Box maxWidth='sm' margin='auto' sx={{ p: 3 }}>
									<Alert severity='error'>{error}</Alert>
								</Box>
							)}
							{/* Google Pay / Apple Pay on hold pending ICE fix — card + PayPal only. */}
							<BraintreeFormInput
								amount={amount}
								isOptOut={!values.saveCard}
								noMobileWallets
								setError={(error) => setError(error)}
							/>
							<Container maxWidth='sm' sx={{ mt: 2 }}>
								<LoadingButton
									type='submit'
									variant='contained'
									color='primary'
									startIcon={<Payment />}
									size='large'
									fullWidth
									sx={(theme) => ({ marginTop: theme.spacing(1) })}
									loading={isSubmitting || isValidating}
									disabled={!isPaymentDetailsValid}
								>
									Validate Details
								</LoadingButton>
							</Container>
						</Form>
					)}
				</Formik>
				<Box maxWidth='sm' sx={{ maxWidth: '400px' }}>
					<DirectPaymentVendors />
				</Box>
				<LoadingModal message='Submitting...' open={submitting} />
			</Paper>
		</Fade>
	);
};

export default BackOfficePaymentSessionPage;
